<script lang="ts" setup>
import { ChipComponent } from '@metapic-apps/shared'
import { ref } from 'vue'

import EmailIcon from '@/assets/icons/mail.svg?component'
import { isEmailValid } from '@/lib/helpers/campaign.helpers'
import { type InvitedUserType } from '@/types/campaign'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'

const tabContent: { id: 'email'; placeholder: string }[] = [
  {
    id: 'email',
    placeholder: 'Email, comma separated',
  },
]

const selectedUsers = defineModel<InvitedUserType[]>('invitedUsers', { default: [] })
const emailInputVal = defineModel<string>('emailInputVal')
const emailSearchRef = ref(null)

const isDuplicate = (items: InvitedUserType[] | undefined, newItem: string) => {
  const identifiers = (items || []).map((item) => item.identifier)
  return identifiers.includes(newItem)
}

const removeUser = (user: InvitedUserType) =>
  (selectedUsers.value = selectedUsers?.value?.filter((item) => item.identifier !== user.identifier))

const handleEmailChange = (inputValue: string) => {
  const isMultipleEntities = inputValue.split(',').length > 1

  if (isMultipleEntities) {
    const usersToAdd: InvitedUserType[] = inputValue
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item && !isDuplicate(selectedUsers.value, item))
      .map((item) => ({
        identifier: item,
        type: 'email',
      }))

    selectedUsers.value = [...selectedUsers.value, ...usersToAdd]
    emailSearchRef?.value?.[0]?.clear()
  } else {
    if (inputValue) {
      if (inputValue.endsWith(' ')) {
        const cleanedValue = inputValue.trim()
        if (!isDuplicate(selectedUsers.value, cleanedValue)) {
          const userToAdd: InvitedUserType = { identifier: cleanedValue, type: 'email' }
          selectedUsers.value = [...selectedUsers.value, ...[userToAdd]]
          emailSearchRef?.value?.[0]?.clear()
        }
      }
    }
  }
}

const handleEmailEnter = (inputValue: string) => {
  const cleanedValue = inputValue.trim()
  if (!isDuplicate(selectedUsers.value, cleanedValue) && cleanedValue) {
    const userToAdd: InvitedUserType = { identifier: cleanedValue, type: 'email' }
    selectedUsers.value = [...selectedUsers.value, ...[userToAdd]]
    emailSearchRef?.value?.[0]?.clear()
  }
}
</script>
<template>
  <form @submit.prevent>
    <div class="tabs">
      <template v-for="tab in tabContent" :key="tab.id">
        <el-input
          ref="emailSearchRef"
          v-model="emailInputVal"
          size="large"
          :placeholder="tab.placeholder"
          class="email-invite-input"
          @input="handleEmailChange"
          @change="handleEmailEnter"
        />
      </template>
      <FlexComponent v-if="selectedUsers.length" class="selected-tags" :gap="8" wrap="wrap">
        <ChipComponent
          v-for="(user, index) in selectedUsers"
          :key="index"
          :variant="user.type === 'email' && !isEmailValid(user.identifier) ? 'danger' : 'default'"
          closable
          @close="removeUser(user)"
        >
          <template #icon>
            <EmailIcon />
          </template>
          {{ user.identifier }}
        </ChipComponent>
      </FlexComponent>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.selected-tags {
  position: relative;
  top: -6px;

  max-height: 390px;
  padding: 14px 8px 8px;
  overflow: auto;

  border-bottom: 1px solid rgba(3, 30, 81, 0.12);
  border-right: 1px solid rgba(3, 30, 81, 0.12);
  border-left: 1px solid rgba(3, 30, 81, 0.12);
  border-radius: 0 0 8px 8px;
}
</style>

<style lang="scss">
.email-invite-input.el-input--large {
  .el-input__wrapper {
    padding: 8px 16px;
    border-radius: 8px;
  }
}
</style>
